import { useEffect, useRef, useState } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { DateTime } from 'luxon';
import {
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  MenuItem,
  TextField,
  styled,
  Typography,
  useTheme,
  Divider,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';

import type { StandingOrderContext } from '../machines/standingOrder.machine';
import { useReservationFormMachines } from '../ReservationFormMachinesContext';
import { useFieldActorSnapshot } from '../useFieldActorSnapshot';
import { TripLegType } from '@/data/models/reservation.model';

const PREFIX = 'StandingOrderForm';

const classes = {
  container: `${PREFIX}-container`,
  standingOrderDescription: `${PREFIX}-standingOrderDescription`,
  standingOrderRange: `${PREFIX}-standingOrderRange`,
  standingOrderDate: `${PREFIX}-standingOrderDate`,
  standingOrderGrid: `${PREFIX}-standingOrderGrid`,
};

const standingOrderDayConfigs = [
  { key: 'monday', labelKey: 'reservation.monday' },
  { key: 'tuesday', labelKey: 'reservation.tuesday' },
  { key: 'wednesday', labelKey: 'reservation.wednesday' },
  { key: 'thursday', labelKey: 'reservation.thursday' },
  { key: 'friday', labelKey: 'reservation.friday' },
  { key: 'saturday', labelKey: 'reservation.saturday' },
  { key: 'sunday', labelKey: 'reservation.sunday' },
] as const;

const StyledBox = styled(Box)(({ theme }) => ({
  [`&.${classes.container}`]: {
    marginTop: theme.spacing(2),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
  },
  [`& .${classes.standingOrderDescription}`]: {
    color: theme.palette.lightTextSecondary.main,
  },
  [`& .${classes.standingOrderRange}`]: {
    marginTop: theme.spacing(1),
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: theme.spacing(2),
    maxWidth: 740,
    [theme.breakpoints.down('sm')]: {
      gridTemplateColumns: '1fr',
    },
  },
  [`& .${classes.standingOrderDate}`]: {
    width: '100%',
  },
  [`& .${classes.standingOrderGrid}`]: {
    maxWidth: 740,
    marginTop: theme.spacing(2),
  },
}));

interface StandingOrderFormProps {
  index: number;
}

export function StandingOrderForm({ index }: StandingOrderFormProps) {
  const { t } = useTranslation('common');
  const theme = useTheme();
  const { register, control, setValue, getFieldState } = useFormContext<any>();
  const [standingOrderRangeError, setStandingOrderRangeError] = useState('');
  const { fieldRefs } = useReservationFormMachines();
  const actor = fieldRefs.standingOrder;
  useFieldActorSnapshot(actor);

  const fieldName = `trips.${index}.standingOrder` as const;
  const standingOrderRaw = useWatch({
    control: control as any,
    name: fieldName as any,
  });
  const standingOrderValue = !!standingOrderRaw;

  const tripType = useWatch({
    control: control as any,
    name: 'tripType' as any,
  }) as unknown as TripLegType | undefined;

  const appointmentDateTime = useWatch({
    control: control as any,
    name: `trips.${index}.appointmentDateTime` as any,
  });

  const appointmentDate = useWatch({
    control: control as any,
    name: `trips.${index}.appointmentDate` as any,
  }) as unknown as string | DateTime | undefined;

  const pickupDateTime = useWatch({
    control: control as any,
    name: `trips.${index}.pickupDateTime` as any,
  });

  const standingOrderStartDate = useWatch({
    control: control as any,
    name: `trips.${index}.standingOrderStartDate` as any,
  });

  const standingOrderEndDate = useWatch({
    control: control as any,
    name: `trips.${index}.standingOrderEndDate` as any,
  });

  const enabledValues = useWatch({
    control: control as any,
    name: standingOrderDayConfigs.map(
      (day) => `trips.${index}.standingOrderDays.${day.key}.enabled` as any,
    ) as any,
  }) as boolean[] | undefined;

  const returnTypeValues = useWatch({
    control: control as any,
    name: standingOrderDayConfigs.map(
      (day) => `trips.${index}.standingOrderDays.${day.key}.returnType` as any,
    ) as any,
  }) as (string | undefined)[] | undefined;

  const roundTripWillCall = useWatch({
    control: control as any,
    name: `trips.${index}.roundTripWillCall` as any,
  }) as unknown as boolean | undefined;

  const roundTripReturnDateTime = useWatch({
    control: control as any,
    name: `trips.${index}.roundTripReturnDateTime` as any,
  }) as unknown as DateTime | null | undefined;

  const previousEnabledValuesRef = useRef<boolean[]>([]);

  useEffect(() => {
    const snapshot = actor.getSnapshot().context as StandingOrderContext;
    if (snapshot.values[index] !== standingOrderValue) {
      actor.send({ type: 'CHANGE', index, value: standingOrderValue });
    }
  }, [actor, index, standingOrderValue]);

  useEffect(() => {
    const nextStartDate =
      typeof appointmentDate === 'string' && appointmentDate.trim()
        ? DateTime.fromISO(appointmentDate)
        : appointmentDate instanceof DateTime && appointmentDate.isValid
          ? appointmentDate
          : appointmentDateTime?.isValid
            ? appointmentDateTime
            : null;

    if (nextStartDate?.isValid) {
      setValue(`trips.${index}.standingOrderStartDate` as any, nextStartDate, {
        shouldDirty: false,
        shouldValidate: true,
      });
      return;
    }

    setValue(`trips.${index}.standingOrderStartDate` as any, null, {
      shouldDirty: false,
      shouldValidate: true,
    });
  }, [appointmentDate, appointmentDateTime, index, setValue]);

  useEffect(() => {
    let message = '';

    if (standingOrderEndDate?.isValid && standingOrderStartDate?.isValid) {
      const daysDiff = standingOrderEndDate.diff(
        standingOrderStartDate,
        'days',
      ).days;
      if (daysDiff <= 0) {
        message = t('reservation.endDateMustBeAfterStartDate');
      } else if (daysDiff > 90) {
        message = t('reservation.max90');
      }
    }

    setStandingOrderRangeError(message);
  }, [standingOrderEndDate, standingOrderStartDate, t]);

  useEffect(() => {
    if (tripType !== TripLegType.RoundTrip) return;
    if (roundTripWillCall === undefined) return;

    const newReturnType = roundTripWillCall ? 'WillCall' : 'Time';

    standingOrderDayConfigs.forEach((day, dayIndex) => {
      const enabled = !!enabledValues?.[dayIndex];
      if (!enabled) return;

      const baseField = `trips.${index}.standingOrderDays.${day.key}` as const;

      setValue(`${baseField}.returnType` as any, newReturnType, {
        shouldDirty: true,
        shouldValidate: true,
      });
    });
  }, [enabledValues, index, setValue, tripType, roundTripWillCall]);

  useEffect(() => {
    const previousEnabledValues = previousEnabledValuesRef.current;

    standingOrderDayConfigs.forEach((day, dayIndex) => {
      const enabled = !!enabledValues?.[dayIndex];
      const wasEnabled = !!previousEnabledValues?.[dayIndex];
      const baseField = `trips.${index}.standingOrderDays.${day.key}` as const;

      if (!enabled && wasEnabled) {
        setValue(`${baseField}.appointmentTime` as any, null, {
          shouldDirty: false,
          shouldValidate: true,
        });
        setValue(`${baseField}.pickupTime` as any, null, {
          shouldDirty: false,
          shouldValidate: true,
        });
        setValue(`${baseField}.returnType` as any, null, {
          shouldDirty: false,
          shouldValidate: true,
        });
        setValue(`${baseField}.returnPickupTime` as any, null, {
          shouldDirty: false,
          shouldValidate: true,
        });
      }

      if (enabled && !wasEnabled) {
        if (appointmentDateTime) {
          setValue(`${baseField}.appointmentTime` as any, appointmentDateTime, {
            shouldDirty: false,
            shouldValidate: true,
          });
        }
        if (pickupDateTime) {
          setValue(`${baseField}.pickupTime` as any, pickupDateTime, {
            shouldDirty: false,
            shouldValidate: true,
          });
        }

        if (
          tripType === TripLegType.RoundTrip &&
          roundTripWillCall !== undefined
        ) {
          const newReturnType = roundTripWillCall ? 'WillCall' : 'Time';
          setValue(`${baseField}.returnType` as any, newReturnType, {
            shouldDirty: false,
            shouldValidate: true,
          });
        }

        if (
          tripType === TripLegType.RoundTrip &&
          !roundTripWillCall &&
          roundTripReturnDateTime instanceof DateTime &&
          roundTripReturnDateTime.isValid
        ) {
          setValue(
            `${baseField}.returnPickupTime` as any,
            roundTripReturnDateTime,
            {
              shouldDirty: false,
              shouldValidate: true,
            },
          );
        }
      }
    });

    previousEnabledValuesRef.current = standingOrderDayConfigs.map(
      (_, dayIndex) => !!enabledValues?.[dayIndex],
    );
  }, [
    appointmentDateTime,
    enabledValues,
    index,
    pickupDateTime,
    roundTripReturnDateTime,
    roundTripWillCall,
    setValue,
    tripType,
  ]);

  useEffect(() => {
    if (!appointmentDateTime) return;

    standingOrderDayConfigs.forEach((day, dayIndex) => {
      if (!enabledValues?.[dayIndex]) return;
      const fieldPath =
        `trips.${index}.standingOrderDays.${day.key}.appointmentTime` as const;
      if (getFieldState(fieldPath as any).isDirty) return;

      setValue(fieldPath as any, appointmentDateTime, {
        shouldDirty: false,
        shouldValidate: true,
      });
    });
  }, [appointmentDateTime, enabledValues, getFieldState, index, setValue]);

  useEffect(() => {
    if (!pickupDateTime) return;

    standingOrderDayConfigs.forEach((day, dayIndex) => {
      if (!enabledValues?.[dayIndex]) return;
      const fieldPath =
        `trips.${index}.standingOrderDays.${day.key}.pickupTime` as const;
      if (getFieldState(fieldPath as any).isDirty) return;

      setValue(fieldPath as any, pickupDateTime, {
        shouldDirty: false,
        shouldValidate: true,
      });
    });
  }, [enabledValues, getFieldState, index, pickupDateTime, setValue]);

  useEffect(() => {
    if (tripType !== TripLegType.RoundTrip) return;
    if (roundTripWillCall) return;
    if (
      !roundTripReturnDateTime ||
      !(roundTripReturnDateTime instanceof DateTime) ||
      !roundTripReturnDateTime.isValid
    )
      return;

    standingOrderDayConfigs.forEach((day, dayIndex) => {
      if (!enabledValues?.[dayIndex]) return;
      const fieldPath =
        `trips.${index}.standingOrderDays.${day.key}.returnPickupTime` as const;
      if (getFieldState(fieldPath as any).isDirty) return;

      setValue(fieldPath as any, roundTripReturnDateTime, {
        shouldDirty: false,
        shouldValidate: true,
      });
    });
  }, [
    enabledValues,
    getFieldState,
    index,
    roundTripReturnDateTime,
    roundTripWillCall,
    setValue,
    tripType,
  ]);

  return (
    <StyledBox className={classes.container}>
      <FormControl component="fieldset" variant="standard">
        <FormControlLabel
          control={
            <Checkbox
              color="primary"
              {...register(fieldName)}
              sx={{ '&.Mui-checked': { color: theme.palette.primary.main } }}
            />
          }
          label={t('reservation.standingOrder')}
        />
        {standingOrderValue && (
          <Typography
            variant="body2"
            className={classes.standingOrderDescription}>
            {t('reservation.standingOrderDescription')}
          </Typography>
        )}

        {standingOrderValue && (
          <Box className={classes.standingOrderRange}>
            <Controller
              name={`trips.${index}.standingOrderStartDate` as any}
              control={control}
              render={({ field }) => (
                <DatePicker
                  label={t('reservation.starts')}
                  value={field.value ?? null}
                  onChange={(value) => field.onChange(value)}
                  disabled
                  slotProps={{
                    textField: {
                      className: classes.standingOrderDate,
                      fullWidth: true,
                      required: true,
                    },
                  }}
                />
              )}
            />
            <Box className={classes.standingOrderDate}>
              <Controller
                name={`trips.${index}.standingOrderEndDate` as any}
                control={control}
                render={({ field }) => (
                  <DatePicker
                    label={t('reservation.ends')}
                    value={field.value ?? null}
                    onChange={(value) => field.onChange(value)}
                    minDate={standingOrderStartDate?.plus?.({ days: 1 })}
                    maxDate={standingOrderStartDate?.plus?.({ days: 90 })}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        required: true,
                      },
                    }}
                  />
                )}
              />
              <FormHelperText error>{standingOrderRangeError}</FormHelperText>
            </Box>
          </Box>
        )}

        {standingOrderValue && (
          <Grid
            container
            columnSpacing={2}
            rowSpacing={1}
            alignItems="flex-start"
            className={classes.standingOrderGrid}>
            <Grid item xs={4}>
              {t('reservation.days')}
            </Grid>
            <Grid item xs={4}>
              {t('reservation.appointment')}
            </Grid>
            <Grid item xs={4}>
              {t('reservation.pickup')}
            </Grid>
            <Grid item xs={12}>
              <Divider />
            </Grid>
            {standingOrderDayConfigs.map((day, dayIndex) => {
              const baseField =
                `trips.${index}.standingOrderDays.${day.key}` as const;
              const enabled = !!enabledValues?.[dayIndex];
              const returnType = returnTypeValues?.[dayIndex];

              return (
                <Grid item container xs={12} key={day.key} columnSpacing={2}>
                  <Grid item xs={4}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          color="primary"
                          {...register(`${baseField}.enabled` as const)}
                          sx={{
                            '&.Mui-checked': {
                              color: theme.palette.primary.main,
                            },
                          }}
                        />
                      }
                      label={t(day.labelKey)}
                    />
                  </Grid>
                  <Grid item xs={4}>
                    {enabled && (
                      <Controller
                        name={`${baseField}.appointmentTime` as any}
                        control={control}
                        render={({ field }) => (
                          <TimePicker
                            label={t('reservation.appointment')}
                            value={field.value ?? null}
                            onChange={(value) => field.onChange(value)}
                            disabled={tripType === TripLegType.RoundTrip}
                            slotProps={{
                              textField: {
                                variant: 'outlined',
                                fullWidth: true,
                              },
                            }}
                          />
                        )}
                      />
                    )}
                  </Grid>
                  <Grid item xs={4}>
                    {enabled && (
                      <Controller
                        name={`${baseField}.pickupTime` as any}
                        control={control}
                        render={({ field }) => (
                          <TimePicker
                            label={t('reservation.pickup')}
                            value={field.value ?? null}
                            onChange={(value) => field.onChange(value)}
                            disabled={tripType === TripLegType.RoundTrip}
                            slotProps={{
                              textField: {
                                variant: 'outlined',
                                fullWidth: true,
                              },
                            }}
                          />
                        )}
                      />
                    )}
                  </Grid>
                  {tripType === TripLegType.RoundTrip && (
                    <>
                      <Grid item xs={4}></Grid>
                      <Grid item xs={4}>
                        {enabled && (
                          <Controller
                            name={`${baseField}.returnType` as any}
                            control={control}
                            render={({ field }) => (
                              <TextField
                                fullWidth
                                select
                                label={t('reservation.returnType')}
                                value={field.value ?? 'WillCall'}
                                onChange={(event) =>
                                  field.onChange(event.target.value)
                                }>
                                <MenuItem value="WillCall">
                                  {t('reservation.returnTypeWillCall')}
                                </MenuItem>
                                <MenuItem value="Time">
                                  {t('reservation.returnTypeTime')}
                                </MenuItem>
                              </TextField>
                            )}
                          />
                        )}
                      </Grid>
                      <Grid item xs={4}>
                        {enabled && returnType === 'Time' && (
                          <Controller
                            name={`${baseField}.returnPickupTime` as any}
                            control={control}
                            render={({ field }) => (
                              <TimePicker
                                label={t('reservation.returnPickup')}
                                value={field.value ?? null}
                                onChange={(value) => field.onChange(value)}
                                slotProps={{
                                  textField: {
                                    variant: 'outlined',
                                    fullWidth: true,
                                  },
                                }}
                              />
                            )}
                          />
                        )}
                      </Grid>
                    </>
                  )}
                </Grid>
              );
            })}
          </Grid>
        )}
      </FormControl>
    </StyledBox>
  );
}
